<template>
  <div>
    <!-- ===== MAIN MODAL: Contract Overview ===== -->
    <b-modal
      id="modal-update-revenue"
      :title="modalTitle"
      size="xl"
      ok-title="Add New Month Data"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      @ok.prevent="openMonthlyForm(null)"
      @hidden="reset"
    >
      <!-- Add/Edit Contract Details button (contractual only) -->
      <b-button
        v-if="candidate && isContractual"
        variant="primary"
        size="sm"
        class="mb-3"
        @click="openContractDetailsForm"
      >
        Add/Edit Contract Details
      </b-button>

      <!-- Permanent / FTE Hiring: one-time revenue -->
      <div v-if="candidate && !isContractual">
        <b-form>
          <b-form-group label="Revenue Amount *">
            <b-form-input v-model.number="permanentForm.revenue" type="number" step="0.01" min="0" placeholder="Enter one-time revenue in ₹" />
          </b-form-group>
          <b-form-group label="Revenue Confirmed *">
            <b-form-select
              v-model="permanentForm.revenue_confirmed"
              :options="revenueConfirmedSelectOptions"
            />
          </b-form-group>
        </b-form>
      </div>

      <!-- Contractual: two-section view -->
      <div v-else-if="candidate && isContractual">
        <!-- Section 1: Contract Detail Data -->
        <h5 class="text-center mb-2">Candidate Contract Detail Data</h5>
        <b-table
          :items="contractDetailRows"
          :fields="contractDetailFields"
          striped
          hover
          responsive
          show-empty
          empty-text="No contract details. Click 'Add/Edit Contract Details' to add."
          class="mb-3"
        >
          <template #cell(actions)="{ item }">
            <feather-icon
              icon="EditIcon"
              size="18"
              class="text-info cursor-pointer"
              @click="openContractDetailsForm(item)"
            />
          </template>
        </b-table>

        <!-- Section 2: Monthly Salary Data -->
        <h5 class="text-center mb-2">Candidate Contract Monthly Salary Data</h5>
        <b-table
          :items="monthlyRecords"
          :fields="monthlyFields"
          striped
          hover
          responsive
          show-empty
          empty-text="No monthly records. Click 'Add New Month Data' to add."
        >
          <template #cell(actions)="{ item }">
            <feather-icon
              v-if="$can('update', 'contractual_candidates')"
              icon="EditIcon"
              size="18"
              class="text-info cursor-pointer"
              @click="openMonthlyForm(item)"
            />
          </template>
        </b-table>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="$bvModal.hide('modal-update-revenue')">Cancel</b-button>
        <b-button
          v-if="candidate && !isContractual"
          variant="primary"
          @click="$emit('save-permanent', { candidate, ...permanentForm })"
        >
          Save Revenue
        </b-button>
        <b-button
          v-if="candidate && isContractual"
          variant="primary"
          @click="openMonthlyForm(null)"
        >
          Add New Month Data
        </b-button>
      </template>
    </b-modal>

    <!-- ===== MODAL 2: Add/Edit Contract Details (SS4) ===== -->
    <b-modal
      id="modal-contract-details"
      :title="modalTitle"
      ok-title="Submit"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      @ok.prevent="submitContractDetails"
    >
      <b-form>
        <b-row>
          <b-col md="6">
            <b-form-group label="Contract Start Date" label-for="cd-start">
              <b-form-input
                id="cd-start"
                v-model="contractDetailsForm.contractStartDate"
                type="date"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Contract End Date" label-for="cd-end">
              <b-form-input
                id="cd-end"
                v-model="contractDetailsForm.contractEndDate"
                type="date"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Rate Card" label-for="cd-rate">
              <b-form-input
                id="cd-rate"
                v-model.number="contractDetailsForm.rateCard"
                type="number"
                step=".01"
                placeholder="Enter Bill Rate Card in Rupees"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Monthly CTC" label-for="cd-ctc">
              <b-form-input
                id="cd-ctc"
                v-model.number="contractDetailsForm.monthlyCTC"
                type="number"
                step=".01"
                placeholder="Enter Gross Monthly CTC of Employee"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Probation Days" label-for="cd-probation">
              <b-form-input
                id="cd-probation"
                v-model="contractDetailsForm.probationDays"
                placeholder="Enter Candidate Probation Days"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>

    <!-- ===== MODAL 3: Add / Edit Monthly Data (SS3) ===== -->
    <b-modal
      id="modal-monthly-data"
      :title="modalTitle"
      ok-title="Submit"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      size="lg"
      @ok.prevent="submitMonthlyData"
    >
      <b-form>
        <b-row>
          <b-col md="6">
            <b-form-group label="Contract Start Date" label-for="md-start">
              <b-form-input
                id="md-start"
                v-model="monthlyForm.contractStartDate"
                type="date"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Contract End Date" label-for="md-end">
              <b-form-input
                id="md-end"
                v-model="monthlyForm.contractEndDate"
                type="date"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Contract Month" label-for="md-month">
              <b-form-select
                id="md-month"
                v-model="monthlyForm.contractMonth"
                :options="monthSelectOptions"
                @change="computeRateAndSalary"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Number of Days / Salary Days" label-for="md-days">
              <b-form-input
                id="md-days"
                v-model.number="monthlyForm.numberOfDays"
                type="number"
                step=".01"
                placeholder="Enter number of Days for Salary"
                @input="computeRateAndSalary"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Rate Card" label-for="md-rate">
              <b-form-input
                id="md-rate"
                v-model.number="monthlyForm.rateCard"
                type="number"
                step=".01"
                placeholder="Enter Bill Rate Card in Rupees"
                @input="computeRateAndSalary"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Billing Amount" label-for="md-billing">
              <b-form-input
                id="md-billing"
                v-model.number="monthlyForm.computedRateCard"
                type="number"
                step=".01"
                placeholder="Computed Rate Card based on Days"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Employee Salary" label-for="md-salary">
              <b-form-input
                id="md-salary"
                v-model.number="monthlyForm.empSalary"
                type="number"
                step=".01"
                placeholder="Enter Employee Salary disbursed in Rupees"
                @input="computeRateAndSalary"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Confirm Salary" label-for="md-confirm-salary">
              <b-form-input
                id="md-confirm-salary"
                v-model.number="monthlyForm.computedSalary"
                type="number"
                step=".01"
                placeholder="Confirm Salary in Rupees based on Days"
                @input="computeRateAndSalary"
              />
              <small
                v-if="monthlyForm.empSalary && monthlyForm.empSalary !== monthlyForm.computedSalary"
                class="text-danger"
              >
                Employee Salary and Confirm Salary should be same
              </small>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Revenue Generated" label-for="md-revenue">
              <b-form-input
                id="md-revenue"
                v-model.number="monthlyForm.revenueGenerated"
                type="number"
                step=".01"
                placeholder="Enter Revenue Generated this Month in Rupees"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Invoice Id" label-for="md-invoice">
              <b-form-input
                id="md-invoice"
                v-model="monthlyForm.invoiceId"
                placeholder="Enter Candidate Invoice ID"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BModal, BForm, BFormGroup, BFormInput, BFormSelect, BRow, BCol, BTable, BButton, VBTooltip
} from 'bootstrap-vue'
import moment from 'moment'

export default {
  name: 'UpdateRevenueModal',
  components: { BModal, BForm, BFormGroup, BFormInput, BFormSelect, BRow, BCol, BTable, BButton },
  directives: { 'b-tooltip': VBTooltip },

  props: {
    monthlyRecords: { type: Array, default: () => [] },
    contractConfig: { type: Object, default: null }
  },

  data() {
    return {
      candidate: null,
      permanentForm: { revenue: null, revenue_confirmed: null },
      revenueConfirmedOptions: [
        { text: 'Yes – Confirmed', value: 'Yes' },
        { text: 'No – Pending', value: 'No' }
      ],

      // Contract details form (SS4)
      contractDetailsForm: {
        contractStartDate: null,
        contractEndDate: null,
        rateCard: null,
        monthlyCTC: null,
        probationDays: null,
        editId: null
      },

      // Monthly data form (SS3)
      monthlyForm: {
        editId: null,
        contractStartDate: null,
        contractEndDate: null,
        contractMonth: null,
        numberOfDays: null,
        rateCard: null,
        computedRateCard: null,
        empSalary: null,
        computedSalary: null,
        revenueGenerated: null,
        invoiceId: null
      },

      // Table field definitions
      contractDetailFields: [
        { key: 'start_date', label: 'Start Date' },
        { key: 'end_date', label: 'End Date' },
        { key: 'rate_card', label: 'Rate Card' },
        { key: 'monthly_ctc', label: 'Monthly CTC' },
        { key: 'probation_days', label: 'Probation Days' },
        { key: 'actions', label: 'Edit', thStyle: { width: '60px' } }
      ],
      monthlyFields: [
        { key: 'month', label: 'Month' },
        { key: 'start_date', label: 'Start Date' },
        { key: 'end_date', label: 'End Date' },
        { key: 'num_of_days', label: 'Num of Days' },
        { key: 'rate_card', label: 'Rate Card' },
        { key: 'computed_rate_card', label: 'Computed Rate Card' },
        { key: 'salary', label: 'Salary' },
        { key: 'computed_salary', label: 'Confirm Salary' },
        { key: 'revenue', label: 'Revenue' },
        { key: 'invoice_id', label: 'Invoice ID' },
        { key: 'actions', label: 'Edit', thStyle: { width: '60px' } }
      ],

      monthsList: []
    }
  },

  computed: {
    isContractual() {
      if (!this.candidate) return false
      const model = String(this.candidate.billing_model || '').toLowerCase()
      return (
        model.includes('contractual') ||
        model.includes('staffing') ||
        model.includes('cost plus') ||
        model.includes('cost+')
      )
    },
    modalTitle() {
      if (!this.candidate) return 'Update Revenue'
      return `${this.candidate.candidate_name || 'Candidate'} – ${this.candidate.client_name || ''}`
    },
    contractDetailRows() {
      if (!this.contractConfig) return []
      return [this.contractConfig]
    },
    revenueConfirmedSelectOptions() {
      return [
        { value: null, text: 'Select' },
        ...this.revenueConfirmedOptions
      ]
    },
    monthSelectOptions() {
      return [
        { value: null, text: 'Mon-YYYY' },
        ...this.monthsList.map(m => ({
          value: m.title || m.text || m.value,
          text: m.title || m.text || String(m.value ?? '')
        }))
      ]
    }
  },

  created() {
    this.monthsList = this.generateMonthsList()
  },

  methods: {
    show(candidateRow) {
      this.candidate = candidateRow
      this.permanentForm = { revenue: candidateRow.revenue_amount || null, revenue_confirmed: null }
      this.resetMonthlyForm()
      this.resetContractDetailsForm()
      this.$nextTick(() => this.$bvModal.show('modal-update-revenue'))
    },

    hide() {
      this.$bvModal.hide('modal-update-revenue')
    },

    reset() {
      this.candidate = null
      this.permanentForm = { revenue: null, revenue_confirmed: null }
      this.resetMonthlyForm()
      this.resetContractDetailsForm()
    },

    // ── Contract Details form ──────────────────────────
    openContractDetailsForm() {
      // Pre-fill from existing config if available
      if (this.contractConfig) {
        this.contractDetailsForm = {
          contractStartDate: this.contractConfig.start_date || null,
          contractEndDate: this.contractConfig.end_date || null,
          rateCard: this.contractConfig.rate_card || null,
          monthlyCTC: this.contractConfig.monthly_ctc || null,
          probationDays: this.contractConfig.probation_days || null,
          editId: this.contractConfig.id || null
        }
      } else {
        this.resetContractDetailsForm()
      }
      this.$bvModal.show('modal-contract-details')
    },

    submitContractDetails() {
      this.$emit('save-contract-details', {
        candidate: this.candidate,
        ...this.contractDetailsForm
      })
      this.$bvModal.hide('modal-contract-details')
    },

    resetContractDetailsForm() {
      this.contractDetailsForm = {
        contractStartDate: null,
        contractEndDate: null,
        rateCard: null,
        monthlyCTC: null,
        probationDays: null,
        editId: null
      }
    },

    // ── Monthly Data form ──────────────────────────────
    openMonthlyForm(existingRow) {
      if (existingRow) {
        // Edit existing record
        this.monthlyForm = {
          editId: existingRow.id,
          contractStartDate: existingRow.start_date || null,
          contractEndDate: existingRow.end_date || null,
          contractMonth: existingRow.month || null,
          numberOfDays: existingRow.num_of_days || null,
          rateCard: existingRow.rate_card || null,
          computedRateCard: existingRow.computed_rate_card || null,
          empSalary: existingRow.salary || null,
          computedSalary: existingRow.computed_salary || null,
          revenueGenerated: existingRow.revenue || null,
          invoiceId: existingRow.invoice_id || null
        }
      } else {
        // New record — pre-fill start/end date and rate card from contractual config
        this.monthlyForm = {
          editId: null,
          contractStartDate: this.contractConfig ? this.contractConfig.start_date : null,
          contractEndDate: this.contractConfig ? this.contractConfig.end_date : null,
          contractMonth: moment().format('MMM-YYYY'),
          numberOfDays: null,
          rateCard: this.contractConfig ? this.contractConfig.rate_card : null,
          computedRateCard: null,
          empSalary: null,
          computedSalary: null,
          revenueGenerated: null,
          invoiceId: null
        }
      }
      this.$bvModal.show('modal-monthly-data')
    },

    submitMonthlyData() {
      this.$emit('save-monthly-data', {
        candidate: this.candidate,
        ...this.monthlyForm
      })
      this.$bvModal.hide('modal-monthly-data')
    },

    resetMonthlyForm() {
      this.monthlyForm = {
        editId: null,
        contractStartDate: null,
        contractEndDate: null,
        contractMonth: null,
        numberOfDays: null,
        rateCard: null,
        computedRateCard: null,
        empSalary: null,
        computedSalary: null,
        revenueGenerated: null,
        invoiceId: null
      }
    },

    // ── Computations (same logic as MyCandidates.vue) ────
    computeRateAndSalary() {
      if (this.monthlyForm.rateCard && this.monthlyForm.numberOfDays && this.monthlyForm.contractMonth) {
        this.monthlyForm.computedRateCard = parseFloat(
          this.computeRateCard(
            this.monthlyForm.rateCard,
            this.monthlyForm.numberOfDays,
            this.monthlyForm.contractMonth
          )
        )
      }
      if (this.monthlyForm.empSalary) {
        this.monthlyForm.revenueGenerated = parseFloat(
          (this.monthlyForm.computedRateCard || 0) - this.monthlyForm.empSalary
        ).toFixed(2)
      }
    },

    computeRateCard(totalRateCard, daysWorked, monthYear) {
      if (!totalRateCard || !daysWorked || !monthYear) return 0
      function getDaysInMonth(month, year) {
        return new Date(year, month, 0).getDate()
      }
      const parts = String(monthYear).split('-')
      if (parts.length < 2) return 0
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const monthIndex = monthNames.indexOf(parts[0])
      const year = parseInt(parts[1], 10)
      if (monthIndex === -1 || isNaN(year)) return 0
      const totalDaysInMonth = getDaysInMonth(monthIndex + 1, year)
      const perDay = totalRateCard / totalDaysInMonth
      return parseFloat(perDay * daysWorked).toFixed(2)
    },

    generateMonthsList() {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const list = []
      const now = new Date()
      for (let i = 24; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
        const label = `${months[d.getMonth()]}-${d.getFullYear()}`
        list.push({ title: label, text: label, value: label })
      }
      return list
    }
  }
}
</script>
